import { RootState } from "@/types";
import * as React from "react";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { useGetAllFeeQuery } from "../../store/feeApi";
import { useGetAllQuery } from "../../store/studentApi";
import { useGetBatchQuery } from "../../store/batchApi";
import { Button } from "../ui/button";
import { Download } from "lucide-react";

export default function ExportReport() {
  const { user } = useSelector((state: RootState) => state.user);
  const { data: allFee } = useGetAllFeeQuery(user?.token);
  const { data: allStudents } = useGetAllQuery(user?.token);
  const { data: allBatches } = useGetBatchQuery();

  const filteredStudents = allFee?.filter((fee: Fee) => fee.isPayable == true);

  const receivedFee = React.useMemo(() => {
    return allFee?.reduce((acc: number, fee: Fee) => acc + fee.paid, 0);
  }, [allFee]);

  const totalExpanse = 10000;

  const studentEnrollmentData = React.useMemo(() => {
    if (!allBatches) return [];

    return allBatches?.map((batch: Batch) => ({
      name: batch.bCode,
      totalStudents:
        allStudents?.filter((student: Student) => student.batch === batch.bCode)
          .length || 0,
      activeBatchStudents:
        allStudents?.filter(
          (student: Student) =>
            student.batch === batch.bCode &&
            student.status.toLowerCase() === "active",
        )?.length || 0,
    }));
  }, [allBatches, allStudents]);

  const exportDashboardReport = () => {
    try {
      // Prepare workbook
      const workbook = XLSX.utils.book_new();

      // 1. Dashboard Summary Sheet
      const summaryData = [
        ["Metric", "Value"],
        ["Total Balance", receivedFee - totalExpanse],
        ["Total Expense", totalExpanse],
        ["Total Received Fees", receivedFee],
        ["Total Batches", allBatches?.length || 0],
        ["Total Students", allStudents?.length || 0],
        [
          "Active Students",
          allStudents?.filter(
            (std: Student) => std.status.toLowerCase() === "active",
          )?.length || 0,
        ],
        [
          "Dropped Students",
          allStudents?.filter(
            (std: Student) => std.status.toLowerCase() === "delete",
          )?.length || 0,
        ],
        [
          "Placed Students",
          allStudents?.filter(
            (std: Student) => std.isPlaced.toLowerCase() === "yes",
          )?.length || 0,
        ],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Dashboard Summary");

      // 2. Pending Fees Sheet
      const pendingFeesData = [
        [
          "Student Name",
          "Batch Code",
          "Course Name",
          "Paid Amount",
          "Pending Amount",
          "Payable Amount",
        ],
        ...(filteredStudents?.map((fee: Fee) => [
          fee.name,
          fee.bCode,
          fee.course,
          fee.paid,
          fee.payable - fee.paid,
          fee.payable,
        ]) || []),
      ];
      const pendingFeesSheet = XLSX.utils.aoa_to_sheet(pendingFeesData);
      XLSX.utils.book_append_sheet(workbook, pendingFeesSheet, "Pending Fees");

      // 3. Top Enrolled Batches Sheet
      const topBatchesData = [
        [
          "Batch Code",
          "Total Students",
          "Active Students",
          "Placed Students",
          "Placement Growth",
        ],
      ];
      const topBatchesSheet = XLSX.utils.aoa_to_sheet(topBatchesData);
      XLSX.utils.book_append_sheet(
        workbook,
        topBatchesSheet,
        "Top Enrolled Batches",
      );

      // 4. Student Enrollment Data Sheet
      const studentEnrollmentSheetData = [
        ["Batch Code", "Total Students", "Active Students"],
        ...(studentEnrollmentData?.map((data) => [
          data.name,
          data.totalStudents,
          data.activeBatchStudents,
        ]) || []),
      ];
      const studentEnrollmentSheet = XLSX.utils.aoa_to_sheet(
        studentEnrollmentSheetData,
      );
      XLSX.utils.book_append_sheet(
        workbook,
        studentEnrollmentSheet,
        "Student Enrollment",
      );

      // Save the workbook
      XLSX.writeFile(
        workbook,
        `AdminDashboard_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
      );

      toast.success("Dashboard Report Exported Successfully");
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export dashboard report");
    }
  };

  return (
    <Button
      onClick={exportDashboardReport}
      className="bg-[#15A249] text-white rounded-lg hover:bg-red-700 transition-colors"
    >
      <Download className="mr-2 h-4 w-4" />
      Export Report
    </Button>
  );
}
